import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, 
  Package, 
  CreditCard, 
  Eye, 
  Search,
  Filter,
  Calendar,
  Euro
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { SimpleSelect } from '../../components/ui/select';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentEnrollments } from '../../services/elearningStudentService';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';
import { supabase } from '../../lib/supabase';
import { 
  getCustomerProfileByToken,
  generateAccessToken 
} from '../../services/customerProfileService';
import { applyDomainFilterToQuery } from '../../hooks/useDomainQuery';

interface Order {
  id: number;
  order_number: string;
  created_at: string;
  status: string;
  total_amount: number;
}

const StudentOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPaidOrders, setTotalPaidOrders] = useState(0);
  const [totalPendingOrders, setTotalPendingOrders] = useState(0);
  const [customerProfile, setCustomerProfile] = useState<any>(null);
  const ordersPerPage = 5;
  
  // Liste des commandes réelles
  const [orders, setOrders] = useState<Order[]>([]);

  const statusConfig = {
    paid: { label: 'Payée', color: 'bg-green-500/20 text-green-400' },
    pending_payment: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400' },
    processing: { label: 'En traitement', color: 'bg-blue-500/20 text-blue-400' },
    shipped: { label: 'Expédiée', color: 'bg-purple-500/20 text-purple-400' },
    completed: { label: 'Terminée', color: 'bg-green-500/20 text-green-400' },
    cancelled: { label: 'Annulée', color: 'bg-red-500/20 text-red-400' }
  };

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    if (profile?.email) {
      loadCustomerOrders(profile.email);
    }
  };

  useEffect(() => {
    if (studentProfile?.email) {
      loadCustomerOrders(studentProfile.email);
    }
  }, [searchQuery, statusFilter, currentPage, studentProfile?.email]);

  const loadCustomerOrders = async (studentEmail: string) => {
    if (!studentEmail) return;
    
    setIsLoading(true);
    try {
      // Générer un token d'accès temporaire pour l'étudiant
      const token = await generateAccessToken(studentEmail);
      if (token) {
        const profile = await getCustomerProfileByToken(token);
        if (profile) {
          setCustomerProfile(profile);
          if (profile.id) {
            await fetchOrders(profile.id);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async (customerId: number) => {
    try {
      // Construire la requête de base
      let queryBase = supabase
        .from('shop_orders')
        .select('*', { count: 'exact' })
        .eq('customer_profile_id', customerId);
      
      queryBase = applyDomainFilterToQuery(queryBase);

      // Appliquer les filtres
      if (searchQuery) {
        queryBase = queryBase.ilike('order_number', `%${searchQuery}%`);
      }
      if (statusFilter) {
        queryBase = queryBase.eq('status', statusFilter);
      }

      // Récupérer le nombre total de commandes pour la pagination
      const { count, error: countError } = await queryBase;
      
      if (countError) throw countError;
      
      if (count !== null) {
        setTotalOrders(count);
      }

      // Récupérer les totaux pour les statistiques
      let allOrdersQuery = supabase
        .from('shop_orders')
        .select('status')
        .eq('customer_profile_id', customerId);
      
      allOrdersQuery = applyDomainFilterToQuery(allOrdersQuery);
      const { data: allOrdersData, error: allOrdersError } = await allOrdersQuery;

      if (allOrdersError) throw allOrdersError;

      if (allOrdersData) {
        const paid = allOrdersData.filter(o => o.status === 'paid' || o.status === 'completed').length;
        const pending = allOrdersData.filter(o => o.status === 'pending_payment').length;
        setTotalPaidOrders(paid);
        setTotalPendingOrders(pending);
      }

      // Récupérer les commandes paginées
      const { data: orders, error } = await queryBase
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage - 1);

      if (error) throw error;
      
      if (orders) {
        setOrders(orders);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
    }
  };



  const handleViewOrder = (orderId: number) => {
    navigate(`/boutique/commandes/${orderId}`);
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending_payment;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (amount: number) => {
    return `${amount.toLocaleString('fr-FR')} FCFA`;
  };

  const handleCardClick = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };



  return (
    <StudentLayout
      title="Mes Commandes"
      description="Consultez l'historique de vos achats et gérez vos commandes"
      onStudentProfileLoaded={handleStudentProfileLoaded}
    >
      <SEOMetaTags 
        title="Mes Commandes - E-Learning"
        description="Consultez l'historique de vos achats et gérez vos commandes."
        keywords="commandes, achats, factures, e-learning"
      />

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200" onClick={() => handleCardClick('')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total commandes</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalOrders}</p>
                  </div>
                  <ShoppingBag className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

        <Card className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200" onClick={() => handleCardClick('paid')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Payées</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPaidOrders}</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

        <Card className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-200" onClick={() => handleCardClick('pending_payment')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En attente</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPendingOrders}</p>
                  </div>
                  <Package className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>

      {/* Filtres et recherche */}
      <Card className="border-0 shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder="Rechercher par numéro de commande..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="md:w-48">
              <SimpleSelect
                value={statusFilter}
                onValueChange={setStatusFilter}
                placeholder="Filtrer par statut"
                options={[
                  { value: '', label: 'Tous les statuts' },
                  { value: 'paid', label: 'Payée' },
                  { value: 'pending_payment', label: 'En attente' },
                  { value: 'processing', label: 'En traitement' },
                  { value: 'shipped', label: 'Expédiée' },
                  { value: 'completed', label: 'Terminée' },
                  { value: 'cancelled', label: 'Annulée' }
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des commandes */}
      <Card className="border-0 shadow-lg">
        <CardContent className="space-y-4 p-6">
          {orders.length > 0 ? (
            <div className="space-y-3">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer group"
                  onClick={() => handleViewOrder(order.id)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Package className="text-blue-500" size={16} />
                      </div>
                      <div>
                        <h4 className="text-gray-900 dark:text-white font-medium">#{order.order_number}</h4>
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 space-x-2">
                          <Calendar size={12} />
                          <span>{formatDate(order.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-900 dark:text-white">
                      <span className="font-semibold">{formatPrice(order.total_amount)}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Eye size={14} className="mr-1" />
                      Voir
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingBag size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-gray-900 dark:text-white font-medium mb-2">Aucune commande</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {searchQuery || statusFilter 
                  ? 'Aucune commande ne correspond à vos critères.'
                  : 'Vous n\'avez pas encore passé de commande.'
                }
              </p>
              {!searchQuery && !statusFilter && (
                <Button
                  onClick={() => navigate('/boutique')}
                >
                  <ShoppingBag className="mr-2" size={16} />
                  Découvrir la boutique
                </Button>
              )}
            </div>
          )}
          
          {/* Pagination */}
          {orders.length > 0 && totalOrders > ordersPerPage && (
            <div className="flex justify-center mt-6 space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Précédent
              </Button>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 px-2">
                Page {currentPage} sur {Math.ceil(totalOrders / ordersPerPage)}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalOrders / ordersPerPage)))}
                disabled={currentPage >= Math.ceil(totalOrders / ordersPerPage)}
              >
                Suivant
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </StudentLayout>
  );
};

export default StudentOrdersPage;
